import React from 'react';
import { Button } from "antd";
import { LineChartOutlined } from "@ant-design/icons";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ReferenceLine,
  ResponsiveContainer,
} from 'recharts';

const DEFAULT_GAS_COLORS = [
  "#ff4d4f", "#1890ff", "#52c41a", "#722ed1",
  "#fa8c16", "#eb2f96", "#13c2c2", "#a0d911"
];

/**
 * 气体监测图表组件 (Function 14)
 */
export const GasChartComponent = ({ data, sensors, onAdvancedClick, showAdvancedButton = true }) => {
  return (
    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
      {/* 详细图表按钮 */}
      {showAdvancedButton && onAdvancedClick && (
        <Button
          type="link"
          size="small"
          icon={<LineChartOutlined />}
          onClick={onAdvancedClick}
          style={{
            position: 'absolute',
            top: 0,
            right: 0,
            zIndex: 10,
            padding: '0 4px',
            fontSize: 12
          }}
        >
          詳細圖表
        </Button>
      )}
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data} margin={{ top: 4, right: 16, left: 0, bottom: 4 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          <XAxis
            dataKey="time"
            tickFormatter={(t) => {
              const d = new Date(t);
              return `${String(d.getHours()).padStart(2, "0")}:${String(
                d.getMinutes()
              ).padStart(2, "0")}`;
            }}
            tick={{ fontSize: 10 }}
            interval="preserveStartEnd"
          />
          <YAxis tick={{ fontSize: 10 }} width={40} />
          <Tooltip
            labelFormatter={(t) => new Date(t).toLocaleString("zh-TW")}
            contentStyle={{ fontSize: 12 }}
          />
          <Legend wrapperStyle={{ fontSize: 11 }} />

          {/* 动态渲染传感器线条 */}
          {Object.entries(sensors || {}).map(([key, sensor], index) => {
            if (!sensor.name || sensor.name.trim() === '') return null;
            
            const dataKeyMap = {
              'a': 'a_val', 'b': 'b_val', 'c': 'c_val', 'd': 'd_val',
              'e': 'e_val', 'f': 'f_val', 'g': 'g_val', 'h': 'h_val'
            };
            const dataKey = dataKeyMap[key];
            const colorIndex = index % DEFAULT_GAS_COLORS.length;
            const color = DEFAULT_GAS_COLORS[colorIndex];
            
            return (
              <Line
                key={key}
                type="monotone"
                dataKey={dataKey}
                name={sensor.name}
                stroke={color}
                dot={false}
                strokeWidth={2}
                connectNulls={false}
              />
            );
          })}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

/**
 * 油压监测图表组件 (Function 7/10)
 */
export const OilChartComponent = ({ data, bounds, onAdvancedClick, showAdvancedButton = true }) => {
  return (
    <div style={{ position: 'relative', width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* 详细图表按钮 */}
      {showAdvancedButton && onAdvancedClick && (
        <Button
          type="link"
          size="small"
          icon={<LineChartOutlined />}
          onClick={onAdvancedClick}
          style={{
            position: 'absolute',
            top: 0,
            right: 0,
            zIndex: 10,
            padding: '0 4px',
            fontSize: 12
          }}
        >
          詳細圖表
        </Button>
      )}
      {/* 图表区域 */}
      <div style={{ flex: 1, minHeight: 0, height: '300px' }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 4, right: 16, left: 0, bottom: 4 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />

            {/* 上下限参考线 */}
            {bounds && (
              <>
                <ReferenceLine
                  y={bounds.lowerBound}
                  label="油壓下限"
                  stroke="red"
                  yAxisId="1"
                />
                <ReferenceLine
                  y={bounds.upperBound}
                  label="油壓上限"
                  stroke="red"
                  yAxisId="1"
                />
              </>
            )}

            <XAxis
              allowDataOverflow
              dataKey="time"
              domain={["dataMin", "dataMax"]}
              type="number"
              tickFormatter={(num) => new Date(num).toLocaleDateString()}
              tickCount={20}
              tick={{ fontSize: 10 }}
            />
            <YAxis
              allowDataOverflow
              domain={[
                bounds ? bounds.lowerBound - 0.5 : "auto",
                bounds ? bounds.upperBound + 0.5 : "auto"
              ]}
              tickFormatter={(value) => value.toFixed(2)}
              type="number"
              yAxisId="1"
              unit="bar"
              tick={{ fontSize: 10 }}
              width={50}
            />
            <YAxis
              orientation="right"
              allowDataOverflow
              domain={[9, 13]}
              type="number"
              yAxisId="2"
              unit="V"
              tick={{ fontSize: 10 }}
              width={45}
            />
            <Tooltip
              labelFormatter={(e) => new Date(e).toLocaleString()}
              contentStyle={{ fontSize: 11 }}
            />
            <Legend wrapperStyle={{ fontSize: 11 }} />

            <Line
              yAxisId="1"
              type="linear"
              dataKey="Rpressure"
              name="R相油壓"
              stroke="blue"
              unit="bar"
              dot={false}
              animationDuration={300}
            />
            <Line
              yAxisId="1"
              type="linear"
              dataKey="Spressure"
              name="S相油壓"
              stroke="green"
              unit="bar"
              dot={false}
              animationDuration={300}
            />
            <Line
              yAxisId="1"
              type="linear"
              dataKey="Tpressure"
              name="T相油壓"
              stroke="black"
              unit="bar"
              dot={false}
              animationDuration={300}
            />
            <Line
              yAxisId="2"
              type="linear"
              dataKey="voltage"
              name="電壓"
              stroke="grey"
              unit="V"
              dot={false}
              animationDuration={300}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* 油压信息显示区 */}
      {bounds && (
        <div style={{ 
          marginTop: 12, 
          paddingTop: 12, 
          borderTop: "1px solid #f0f0f0",
          display: "flex",
          gap: 16,
          fontSize: 13,
          color: "#666"
        }}>
          <div>
            <span style={{ fontWeight: 500, color: "#262626" }}>油壓上限：</span>
            <span style={{ color: "#ff4d4f" }}>{bounds.upperBound}</span>
          </div>
          <div>
            <span style={{ fontWeight: 500, color: "#262626" }}>油壓下限：</span>
            <span style={{ color: "#1890ff" }}>{bounds.lowerBound}</span>
          </div>
          {bounds.tolerance !== undefined && (
            <div>
              <span style={{ fontWeight: 500, color: "#262626" }}>下限放寬：</span>
              <span>{bounds.tolerance}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/**
 * 简单图表组件 (单折线)
 */
export const SimpleChartComponent = ({ data, title, onAdvancedClick, showAdvancedButton = true }) => {
  return (
    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
      {/* 详细图表按钮 */}
      {showAdvancedButton && onAdvancedClick && (
        <Button
          type="link"
          size="small"
          icon={<LineChartOutlined />}
          onClick={onAdvancedClick}
          style={{
            position: 'absolute',
            top: 0,
            right: 0,
            zIndex: 10,
            padding: '0 4px',
            fontSize: 12
          }}
        >
          詳細圖表
        </Button>
      )}
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data} margin={{ top: 4, right: 16, left: 0, bottom: 4 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          <XAxis
            dataKey="time"
            tickFormatter={(t) => {
              const d = new Date(t);
              return `${String(d.getHours()).padStart(2, "0")}:${String(
                d.getMinutes()
              ).padStart(2, "0")}`;
            }}
            tick={{ fontSize: 11 }}
            interval="preserveStartEnd"
          />
          <YAxis tick={{ fontSize: 11 }} width={48} />
          <Tooltip
            labelFormatter={(t) => new Date(t).toLocaleString("zh-TW")}
            formatter={(v) => [v, title || "數值"]}
          />
          <Line
            type="monotone"
            dataKey="value"
            stroke="#1890ff"
            dot={false}
            strokeWidth={2}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

/**
 * 水情监测图表组件 (Function 11)
 * 显示三条线：水位、平均流速、流量
 */
export const WaterChartComponent = ({ data, onAdvancedClick, showAdvancedButton = true }) => {
  return (
    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
      {/* 详细图表按钮 */}
      {showAdvancedButton && onAdvancedClick && (
        <Button
          type="link"
          size="small"
          icon={<LineChartOutlined />}
          onClick={onAdvancedClick}
          style={{
            position: 'absolute',
            top: 0,
            right: 0,
            zIndex: 10,
            padding: '0 4px',
            fontSize: 12
          }}
        >
          詳細圖表
        </Button>
      )}
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data} margin={{ top: 4, right: 16, left: 0, bottom: 4 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
        <XAxis
          dataKey="time"
          tickFormatter={(t) => {
            const d = new Date(t);
            return `${String(d.getHours()).padStart(2, "0")}:${String(
              d.getMinutes()
            ).padStart(2, "0")}`;
          }}
          tick={{ fontSize: 11 }}
          interval="preserveStartEnd"
        />
        <YAxis tick={{ fontSize: 11 }} width={48} />
        <Tooltip
          labelFormatter={(t) => new Date(t).toLocaleString("zh-TW")}
          formatter={(value, name) => {
            const nameMap = {
              water_level: '水位',
              average_velocity: '平均流速',
              discharge: '流量'
            };
            return [value, nameMap[name] || name];
          }}
        />
        <Legend 
          formatter={(value) => {
            const nameMap = {
              water_level: '水位',
              average_velocity: '平均流速',
              discharge: '流量'
            };
            return nameMap[value] || value;
          }}
        />
        <Line
          type="monotone"
          dataKey="water_level"
          name="water_level"
          stroke="#1890ff"
          dot={false}
          connectNulls
          animationDuration={300}
        />
        <Line
          type="monotone"
          dataKey="average_velocity"
          name="average_velocity"
          stroke="#52c41a"
          dot={false}
          connectNulls
          animationDuration={300}
        />
        <Line
          type="monotone"
          dataKey="discharge"
          name="discharge"
          stroke="#fa8c16"
          dot={false}
          connectNulls
          animationDuration={300}
        />
      </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

/**
 * 紅外線熱像監測圖表組件 (Function 15)
 * 包含溫度圖 (3條實測溫度線 + 最高/最低溫) 和濕度圖
 */
export const Function15ChartComponent = ({ data, onAdvancedClick, showAdvancedButton = true }) => {
  if (!data || !data.history || data.history.length === 0) {
    return <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>暫無數據</div>;
  }

  const formatTime = (t) => {
    if (!t) return "";
    const d = new Date(t);
    return `${d.getMonth() + 1}/${d.getDate()} ${String(d.getHours()).padStart(2, "0")}:00`;
  };

  // ✨ 合併 temp_bands 到 history（與 function15.js 相同邏輯）
  const buildChartSeries = () => {
    const bandMap = {};
    (data.temp_bands || []).forEach((b) => {
      const ts = new Date(b.bucket).getTime();
      const key = Math.floor(ts / 21600000) * 21600000; // 6 hour bucket
      bandMap[key] = {
        temp_max: b.temp_max != null ? parseFloat(Number(b.temp_max).toFixed(2)) : null,
        temp_min: b.temp_min != null ? parseFloat(Number(b.temp_min).toFixed(2)) : null,
      };
    });

    return (data.history || []).map((h) => {
      const ts = new Date(h.time).getTime();
      const key = Math.floor(ts / 21600000) * 21600000;
      const band = bandMap[key] || {};
      return {
        time: formatTime(h.time),
        humidity: h.humidity != null ? parseFloat(Number(h.humidity).toFixed(2)) : null,
        camera_temp_1: h.camera_temp_1 != null ? parseFloat(Number(h.camera_temp_1).toFixed(2)) : null,
        camera_temp_2: h.camera_temp_2 != null ? parseFloat(Number(h.camera_temp_2).toFixed(2)) : null,
        camera_temp_3: h.camera_temp_3 != null ? parseFloat(Number(h.camera_temp_3).toFixed(2)) : null,
        temp_max: band.temp_max ?? null,
        temp_min: band.temp_min ?? null,
      };
    });
  };

  const chartSeries = buildChartSeries();

  const ChartTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    const maxEntry = payload.find((p) => p.dataKey === "temp_max");
    const minEntry = payload.find((p) => p.dataKey === "temp_min");
    const diff = maxEntry?.value != null && minEntry?.value != null
      ? parseFloat((maxEntry.value - minEntry.value).toFixed(2))
      : null;
    return (
      <div style={{ background: "#fff", border: "1px solid #eee", padding: "8px 12px", borderRadius: 6, fontSize: 12 }}>
        <div style={{ marginBottom: 4, color: "#666" }}>{label}</div>
        {payload.map((p) => (
          <div key={p.dataKey} style={{ color: p.color }}>
            {p.name}：{p.value != null ? p.value : "—"}
            {["camera_temp_1", "camera_temp_2", "camera_temp_3", "temp_max", "temp_min"].includes(p.dataKey) ? "°C" : p.dataKey === "humidity" ? "%" : ""}
          </div>
        ))}
        {diff != null && (
          <div style={{ color: "#888", marginTop: 4, borderTop: "1px solid #f0f0f0", paddingTop: 4 }}>
            溫差：{diff}°C
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
      {showAdvancedButton && onAdvancedClick && (
        <Button
          type="link"
          size="small"
          icon={<LineChartOutlined />}
          onClick={onAdvancedClick}
          style={{
            position: 'absolute',
            top: 0,
            right: 0,
            zIndex: 10,
            padding: '0 4px',
            fontSize: 12
          }}
        >
          詳細圖表
        </Button>
      )}
      
      {/* 溫度圖 */}
      <ResponsiveContainer width="100%" height="50%">
        <LineChart data={chartSeries} margin={{ top: 4, right: 16, left: 0, bottom: 4 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          <XAxis
            dataKey="time"
            tick={{ fontSize: 10 }}
            interval="preserveStartEnd"
          />
          <YAxis tick={{ fontSize: 10 }} width={40} unit="°C" />
          <Tooltip content={<ChartTooltip />} />
          <Legend iconSize={12} wrapperStyle={{ fontSize: 11 }} />
          <Line type="monotone" dataKey="camera_temp_1" name="實測1" stroke="#389e0d" dot={false} strokeWidth={2} connectNulls />
          <Line type="monotone" dataKey="camera_temp_2" name="實測2" stroke="#d4b106" dot={false} strokeWidth={2} connectNulls />
          <Line type="monotone" dataKey="camera_temp_3" name="實測3" stroke="#d46b08" dot={false} strokeWidth={2} connectNulls />
          <Line type="monotone" dataKey="temp_max" name="最高溫" stroke="#ff4d4f" dot={false} strokeWidth={1.5} strokeDasharray="5 4" connectNulls />
          <Line type="monotone" dataKey="temp_min" name="最低溫" stroke="#4096ff" dot={false} strokeWidth={1.5} strokeDasharray="5 4" connectNulls />
        </LineChart>
      </ResponsiveContainer>

      {/* 濕度圖 */}
      <ResponsiveContainer width="100%" height="50%">
        <LineChart data={chartSeries} margin={{ top: 4, right: 16, left: 0, bottom: 4 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          <XAxis
            dataKey="time"
            tick={{ fontSize: 10 }}
            interval="preserveStartEnd"
          />
          <YAxis tick={{ fontSize: 10 }} width={40} unit="%" domain={[0, 100]} />
          <Tooltip content={<ChartTooltip />} />
          <Legend iconSize={12} wrapperStyle={{ fontSize: 11 }} />
          <Line type="monotone" dataKey="humidity" name="濕度" stroke="#1890ff" dot={false} strokeWidth={2} connectNulls />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};
